/**
 * Scheduled saved views (#1115 "Email me this view"): the owner picks Off / Daily / Weekly on a weekday (Mon-Fri), saved
 * as the view's `schedule` (null stops it). Delivery is the API's job; this file only reads and writes the field.
 */
import { cn, Select, type SelectOption } from '@patchbay/ui'
import { Clock } from 'lucide-react'
import type { ViewSchedule } from '../../lib/api/generated'

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const

const SCHEDULE_OPTIONS: readonly SelectOption[] = [
  { value: 'off', label: 'Off' },
  { value: 'daily', label: 'Daily' },
  ...WEEKDAYS.map((day, i) => ({ value: `weekly-${i}`, label: `Weekly on ${day}` })),
]

export const DELIVERY_COPY = 'Delivered to your Patchbay inbox at 7:00, and by email if your notification settings send report emails.'

/** "Daily" / "Weekly · Tue"; null when not scheduled. */
export function scheduleLabel(schedule: ViewSchedule | null | undefined): string | null {
  if (!schedule) return null
  if (schedule.cadence === 'daily') return 'Daily'
  return `Weekly · ${(WEEKDAYS[schedule.weekday ?? 0] ?? WEEKDAYS[0]).slice(0, 3)}`
}

/** The select's value for a schedule: `off`, `daily` or `weekly-<0..4>`. */
export function scheduleValue(schedule: ViewSchedule | null | undefined): string {
  if (!schedule) return 'off'
  return schedule.cadence === 'daily' ? 'daily' : `weekly-${Math.min(4, Math.max(0, schedule.weekday ?? 0))}`
}

export function parseScheduleValue(value: string): ViewSchedule | null {
  if (value === 'daily') return { cadence: 'daily' }
  const m = /^weekly-([0-4])$/.exec(value)
  return m ? { cadence: 'weekly', weekday: Number(m[1]) } : null
}

/** A small clock + "Daily" / "Weekly · Tue" on a scheduled view (menu rows, pinned chips). */
export function ScheduleBadge({ schedule, className }: { schedule: ViewSchedule | null | undefined; className?: string }) {
  const label = scheduleLabel(schedule)
  if (!label) return null
  return (
    <span data-schedule={label} className={cn('inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground', className)}>
      <Clock aria-hidden className="size-3" />
      <span className="sr-only">Emailed </span>
      {label}
    </span>
  )
}

export function ScheduleSelect({
  schedule,
  onChange,
  disabled,
  label = 'Email me this view',
}: {
  schedule: ViewSchedule | null | undefined
  onChange: (schedule: ViewSchedule | null) => void
  disabled?: boolean
  /** The accessible name (the view's name makes it unique in a list). */
  label?: string
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span aria-hidden>Email me</span>
      <Select
        aria-label={label}
        className="h-7 text-xs"
        value={scheduleValue(schedule)}
        disabled={disabled}
        options={SCHEDULE_OPTIONS}
        onValueChange={(next) => onChange(parseScheduleValue(next))}
      />
    </div>
  )
}
